import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Tabs,
  Tab,
  Divider,
  Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  Chat as ChatIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Help as HelpIcon,
  Error as ErrorIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipPrevious as RewindIcon,
  SkipNext as FastForwardIcon,
  Speed as SpeedIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { getConversationDetails } from '../../../redux/actions/conversationsActions';

// Custom TabPanel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`conversation-tabpanel-${index}`}
      aria-labelledby={`conversation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const ConversationDetailSidebar = ({ open, onClose, conversationId }) => {
  const dispatch = useDispatch();
  const { conversationDetails, detailsLoading, detailsError } = useSelector((state) => state.conversations);
  const { webhookData } = useSelector((state) => state.billing);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (open && conversationId) {
      setIsLoading(true);
      dispatch(getConversationDetails(conversationId))
        .finally(() => setIsLoading(false));
    }
  }, [open, conversationId, dispatch]);

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Format date from Unix timestamp in America/New_York (EST/EDT), no tz suffix
  const formatDate = (unixTimestamp) => {
    if (!unixTimestamp) return 'N/A';
    const date = new Date(unixTimestamp * 1000);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    }).format(date);
  };

  // Format duration from seconds
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get status color and icon for chip
  const getStatusInfo = (status) => {
    switch (status) {
      // case 'success':
      case 'success':
        return { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
      case 'failed':
      case 'Failed':
      case 'failure':
        return { color: 'error', icon: <ErrorIcon sx={{ fontSize: 16 }} /> };
      case 'unknown':
        return { color: 'default', icon: <HelpIcon sx={{ fontSize: 16 }} /> };
      default:
        return { color: 'default', icon: <HelpIcon sx={{ fontSize: 16 }} /> };
    }
  };

  // Function to render markdown links, plain URLs, and images in message content
  const renderMessageWithLinks = (message) => {
    if (!message) return "Agent is thinking...";
    
    // Regular expression to match both markdown links [text](url) and plain/bare URLs
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    // Matches: https://..., http://..., www.example.com/..., example.com/path
    const urlRegex = /\b((?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[\w\-._~:/?#\[\]@!$&'()*+,;=%]*)?)/g;
    
    // Function to check if URL is an image
    const isImageUrl = (url) => {
      const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg|ico)(\?.*)?$/i;
      return imageExtensions.test(url);
    };
    
    // Find all matches (both markdown and plain URLs) and sort by position
    const allMatches = [];
    
    // Find markdown links
    let match;
    while ((match = markdownLinkRegex.exec(message)) !== null) {
      // Only accept http/https links for markdown
      if (/^https?:\/\//i.test(match[2])) {
        allMatches.push({
          type: 'markdown',
          start: match.index,
          end: match.index + match[0].length,
          text: match[1],
          url: match[2],
          fullMatch: match[0]
        });
      }
    }
    
    // Reset regex
    urlRegex.lastIndex = 0;
    
    // Find plain URLs
    while ((match = urlRegex.exec(message)) !== null) {
      // Check if this URL is not already part of a markdown link
      const isInMarkdown = allMatches.some(md => 
        match.index >= md.start && match.index < md.end
      );
      
      if (!isInMarkdown) {
        const detected = match[1];
        const href = /^https?:\/\//i.test(detected) ? detected : `https://${detected}`;
        // Skip if this is part of an email address like name@domain.com
        const charBeforeUrl = message[match.index - 1] || '';
        if (charBeforeUrl === '@') {
          continue;
        }
        // Check if this URL is an image
        const urlType = isImageUrl(href) ? 'image' : 'url';
        allMatches.push({
          type: urlType,
          start: match.index,
          end: match.index + match[0].length,
          text: detected,
          url: href,
          fullMatch: match[0]
        });
      }
    }
    
    // Sort matches by position
    allMatches.sort((a, b) => a.start - b.start);
    
    // Build the result
    const parts = [];
    let lastIndex = 0;
    
    allMatches.forEach((match, index) => {
      // Add text before the link
      if (match.start > lastIndex) {
        parts.push(message.slice(lastIndex, match.start));
      }
      
      // Add the link component
      // If the link immediately follows a colon (e.g., "Label: url"),
      // insert a newline so the link renders on the next line.
      const charBefore = message[match.start - 1] || '';
      const twoBefore = message.slice(Math.max(0, match.start - 2), match.start);
      const threeBefore = message.slice(Math.max(0, match.start - 3), match.start);
      const precededByColon = /:\s?$/.test(twoBefore) || /:\s?$/.test(threeBefore);
      const precededByNewline = charBefore === '\n';
      if (!precededByNewline && precededByColon) {
        parts.push('\n');
      }

      // Render images or links based on type
      if (match.type === 'image') {
        parts.push(
          <Box
            key={`image-${index}`}
            sx={{
              mt: 1,
              mb: 1,
              display: 'inline-block'
            }}
          >
            <img
              src={match.url}
              alt={match.text}
              style={{
                maxWidth: '150px',
                maxHeight: '150px',
                height: 'auto',
                display: 'block',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
              onError={(e) => {
                // Hide image if it fails to load
                e.target.style.display = 'none';
              }}
            />
          </Box>
        );
      } else {
        parts.push(
          <Link
            key={`link-${index}`}
            href={match.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#3b82f6',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1d4ed8'
              }
            }}
          >
            {match.text}
          </Link>
        );
      }
      
      lastIndex = match.end;
    });
    
    // Add remaining text after the last link
    if (lastIndex < message.length) {
      parts.push(message.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : message;
  };

  const statusInfo = getStatusInfo(
    // conversationDetails?.analysis?.call_successful
    conversationDetails?.metadata?.termination_reason === "No user message received for a long period of time - probably a network error occurred." ?
    "success" :
    conversationDetails?.analysis?.call_successful
  );
    
  // Audio player controls
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    // Here you would integrate with actual audio playback
  };

  const handleRewind = () => {
    setCurrentTime(Math.max(0, currentTime - 10));
  };

  const handleFastForward = () => {
    setCurrentTime(Math.min(duration, currentTime + 10));
  };

  const webhookPerCredit = webhookData?.getCosting[0]?.percredit || 0

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', sm: '600px', md: '800px' },
          maxWidth: '90vw',
          boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.1)',
          bgcolor: '#f8fafc',
          marginTop: '64px', // Adjust based on your header height
          height: 'calc(100% - 64px)'
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb', bgcolor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              bgcolor: 'rgba(0, 0, 0, 0.87)',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}>
              <ChatIcon sx={{ fontSize: 20, color: '#ffffff' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Conversation with {conversationDetails?.agent_id ? 'Agent' : 'Unknown Agent'}
              </Typography>
              {conversationId && (
                <Typography variant="body2" sx={{ color: '#6b7280', fontFamily: 'monospace', fontSize: '12px' }}>
                  {conversationId}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ overflow: 'auto', height: 'calc(100vh - 120px)' }}>
        {isLoading || detailsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : detailsError ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: '#ef4444', mb: 1 }}>
              Error loading conversation details
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {detailsError}
            </Typography>
          </Box>
        ) : conversationDetails ? (
          <Box sx={{ p: 3 }}>
            {/* Audio Player */}
            {/* {conversationDetails.has_audio && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <IconButton 
                    onClick={handlePlayPause}
                    sx={{ 
                      bgcolor: '#3b82f6', 
                      color: 'white',
                      '&:hover': { bgcolor: '#2563eb' }
                    }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  </IconButton>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small" onClick={handleRewind}>
                      <RewindIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleFastForward}>
                      <FastForwardIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <SpeedIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      1.0x
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  height: 60, 
                  bgcolor: '#e5e7eb', 
                  borderRadius: 1, 
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Audio Waveform
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {formatDuration(currentTime)} / {formatDuration(conversationDetails.metadata?.call_duration_secs || 0)}
                  </Typography>
                </Box>
              </Paper>
            )} */}

            {/* Main Content and Metadata Side by Side */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Left Content */}
              <Box sx={{ flex: 1 }}>
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="conversation tabs">
                    <Tab label="Overview" />
                    <Tab label="Transcription" />
                    <Tab label="Client data" />
                  </Tabs>
                </Box>

                {/* Tab Content */}
                <TabPanel value={tabValue} index={0}>
                  {/* Summary */}
                  {conversationDetails.analysis?.transcript_summary && (
                    <Paper sx={{ p: 3, mb: 3, bgcolor: '#ffffff' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Summary
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                        {conversationDetails.analysis.transcript_summary}
                      </Typography>
                    </Paper>
                  )}

                  {/* Call Status */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: '#ffffff' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Call status
                    </Typography>
                    <Chip
                      // label={
                      //   conversationDetails?.status === "done" && conversationDetails?.analysis?.call_successful  === "unknown" ?
                      //   "Unknown" :
                      //   conversationDetails?.status === "done" && conversationDetails?.analysis?.call_successful  === "success" ?
                      //   "Success" :
                      //   conversationDetails?.status === "in-progress" ?
                      //   "Ongoing" :
                      //   conversationDetails?.status || "-"
                      // }
                      label={
                        conversationDetails?.metadata?.termination_reason === "No user message received for a long period of time - probably a network error occurred." ?
                        "success" :
                        conversationDetails?.analysis?.call_successful
                      }
                      color={statusInfo.color}
                      // icon={statusInfo.icon}
                      size="small"
                      variant="outlined"
                    />
                  </Paper>

                  {/* User ID */}
                  {/* <Paper sx={{ p: 3, bgcolor: '#ffffff' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      User ID
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>
                      {conversationDetails.user_id || 'No user ID'}
                    </Typography>
                  </Paper> */}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  {/* Transcription */}
                  {conversationDetails.transcript && conversationDetails.transcript.length > 0 ? (
                    <Box sx={{ 
                      maxHeight: '600px', 
                      overflowY: 'auto', 
                      p: 2,
                      bgcolor: '#ffffff',
                      borderRadius: 2
                    }}>
                      {conversationDetails.transcript.map((message, index) => {
                        const isAgent = message.role === 'agent';
                        return (
                          (message.original_message || message.message) && (
                          <Box 
                            key={index} 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: isAgent ? 'flex-start' : 'flex-end',
                              mb: 2
                            }}
                          >
                           
                            <Box sx={{ 
                              display: 'flex',
                              flexDirection: isAgent ? 'row' : 'row-reverse',
                              alignItems: 'flex-start',
                              gap: 1,
                              maxWidth: '100%'
                            }}>
                              {/* Icon outside bubble */}
                              <Box sx={{ flexShrink: 0, mt: '2px' }}>
                                {isAgent ? (
                                  <SupportAgentIcon sx={{ fontSize: 24, color: '#6b7280' }} />
                                ) : (
                                  <PersonIcon sx={{ fontSize: 24, color: '#6b7280' }} />
                                )}
                              </Box>

                              {/* Message column */}
                             
                             
                             <Box sx={{ 
                                maxWidth: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isAgent ? 'flex-start' : 'flex-end'
                              }}>
                                {/* Message bubble */}
                                <Paper sx={{ 
                                  p: 2, 
                                  pb: 3, // space for timestamp in corner
                                  bgcolor: isAgent ? 'rgba(0, 0, 0, 0.02)' : 'rgba(0, 0, 0, 0.06)',
                                  borderRadius: isAgent ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
                                  position: 'relative'
                                }}>
                                  <Typography variant="body1" sx={{ 
                                    color: '#374151',
                                    lineHeight: 1.5,
                                    fontSize: '14px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {renderMessageWithLinks(message.original_message || message.message)}
                                  </Typography>

                                  {/* Time in bottom corner inside bubble */}
                                  <Typography variant="caption" sx={{ 
                                    position: 'absolute', 
                                    bottom: 6, 
                                    right:  8,
                                    left: 'auto',
                                    color: '#9ca3af'
                                  }}>
                                    {formatDuration(message.time_in_call_secs)}
                                  </Typography>
                                </Paper>
                              </Box>
                            
                            </Box>
                            
                          </Box>
                        )
                        );
                      })}
                    </Box>
                  ) : (
                    <Paper sx={{ p: 3, bgcolor: '#ffffff' }}>
                      <Typography variant="body1" sx={{ color: '#6b7280', textAlign: 'center' }}>
                        No transcription available
                      </Typography>
                    </Paper>
                  )}
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                  {/* Client Data */}
                  <Paper sx={{ p: 3, bgcolor: '#ffffff' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Client Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                          Source
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#374151' }}>
                          {conversationDetails.conversation_initiation_client_data?.source_info?.source || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                          Version
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#374151' }}>
                          {conversationDetails.conversation_initiation_client_data?.source_info?.version || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </TabPanel>
              </Box>

              {/* Right Metadata */}
              <Box sx={{ width: 280, flexShrink: 0 }}>
                <Paper sx={{ p: 3, bgcolor: '#ffffff', position: 'sticky', top: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Metadata
                  </Typography>

                  <Box sx={{ space: 2 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Date
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                        {formatDate(conversationDetails.metadata?.start_time_unix_secs)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Duration
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                        {formatDuration(conversationDetails?.metadata?.call_duration_secs)}
                      </Typography>
                    </Box>

                    {/* <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Credits (Call)
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                        {conversationDetails?.metadata?.charging?.call_charge || 0}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Credits (LLM)
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                        {conversationDetails?.metadata?.charging?.llm_charge || 0}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Cost (Call)
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                        {(webhookPerCredit * (conversationDetails?.metadata?.charging?.call_charge)).toFixed(3)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Cost (LLM)
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                        {(conversationDetails?.metadata?.charging?.llm_price || 0).toFixed(3)}
                        
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                        Total Credits Cost
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#374151' }}>
                      US { `$${((webhookPerCredit * (conversationDetails?.metadata?.charging?.call_charge))+ (conversationDetails?.metadata?.charging?.llm_price)).toFixed(3)}`}
                      </Typography>
                     
                    </Box> */}

                    {/* {conversationDetails.metadata?.termination_reason && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                          Termination Reason
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#ef4444', fontSize: '12px' }}>
                          {conversationDetails.metadata.termination_reason}
                        </Typography>
                      </Box>
                    )} */}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: '#6b7280' }}>
              No conversation details available
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ConversationDetailSidebar;
